import { getFloorById, AreaWithSensorInfo } from "@lib/store";
import { StyledModalCellItemIcon, StyledTruncatedText, Wrapper } from "./styles";
import { FlexCol, FlexRow, P } from "@quarks";
import { useTheme } from "styled-components";
import { useAppSelector, useAreaSensorInfo } from "@hooks";
import { useMemo } from "react";
import { AvatarStack } from "@molecules";
import { HaNWorkspaceIcon } from "../HaNWorkspaceIcon";
import { HaNWorkspaceLabel } from "../HaNWorkspaceLabel";
import { dateToLocalDateString, dateToLocalDateTimeString } from "@lib/store/src/data/timeslots/dateTimeHelpers";
import { useConnectionReservationsWithUsers } from "@lib/store/src/ui/hooks/timeslots/useConnectionReservationsWithUsers";
import { useHereAndNow } from "@pages";

type HaNWorkspaceCellProps = {
  area: AreaWithSensorInfo;
  onClick: (area: AreaWithSensorInfo) => void;
};

export const HaNWorkspaceCell = ({ area, onClick }: HaNWorkspaceCellProps) => {
  const theme = useTheme();
  const { hoverRoomId, selectedObject } = useHereAndNow();
  const floor = useAppSelector(state => getFloorById(state, area.floorId))!;
  const selectorProps = useMemo(() => ({ date: new Date().toISOString(), nodeId: area.id }), [area.id]);

  // Connections
  const now = new Date();
  const todayKey = dateToLocalDateString(now);
  const nowKey = dateToLocalDateTimeString(now); // TODO: this should ideally increment every now and then
  const connections_v2 = useConnectionReservationsWithUsers(todayKey) // TODO: this should be refetched every now and then
    // Ongoing
    .filter(data => nowKey >= data.reservation.localStart && nowKey <= data.reservation.localEnd)
    .filter(data => "areaId" in data.reservation.workspace && data.reservation.workspace.areaId === area.id);

  const { areaSensorInfo } = useAreaSensorInfo(area.id) ?? {};

  const memoizedAvatarStack = useMemo(() => {
    const users = connections_v2.map(c => c.user);
    if (users.length === 0) return null;
    return (
      <AvatarStack
        date={selectorProps.date}
        users={users}
        overlap={true}
      />
    );
  }, [selectorProps.date, connections_v2]);

  return (
    <Wrapper
      tabIndex={1}
      onClick={() => onClick(area)}
      data-hover={hoverRoomId === area.id || selectedObject?.id === area.id}
      onKeyDown={e => {
        if (e.key === "Enter") onClick(area);
      }}>
      <div className="flex flex-col gap-2 w-full">
        <div className="flex justify-between items-center ">
          <FlexRow
            gap={8}
            alignItems="center"
            width="100%"
            flexWrap="wrap">
            <FlexRow
              gap={8}
              justifyContent="flex-start"
              className="w-3/4">
              <StyledModalCellItemIcon
                backgroundColor={theme.card.beige500.background}
                icon={"desk"}
              />
              <FlexCol
                justifyContent="center"
                gap={2}
                overflow="hidden">
                <StyledTruncatedText fontWeight="bold">{area.name}</StyledTruncatedText>
                <P>{floor.name}</P>
                {memoizedAvatarStack}
              </FlexCol>
            </FlexRow>

            {areaSensorInfo && (
              <FlexCol>
                <HaNWorkspaceLabel status={areaSensorInfo.status} />
              </FlexCol>
            )}
          </FlexRow>
        </div>
      </div>
    </Wrapper>
  );
};
